// Custom acrylic awards and recognition plaques. Targets (SERPs checked
// 2026-11-03): custom acrylic awards 390 ($10.50), employee recognition
// plaques 210 ($14.78), acrylic awards 1,000. "Employee recognition awards"
// (1,600) returns articles on recognition programs, so it is secondary. Also:
// crystal awards 1,600, years of service
// awards 590, corporate awards 480, custom acrylic awards 390, service awards
// for employees 320, sales awards 210; CPC $9–$32. Award shops rank (Decade,
// US Acrylic Awards, Crown). Images avoid crystal and clear acrylic (owner,
// 2026-11-02): the studio's angle is cut, layered, printed and sculpted awards.
const { pricing: trophyPricing } = require('../custom-trophy')
const {
  quoted,
  setup,
  each,
  medium,
  costAnswer,
  timingAnswer,
  minimumAnswer,
  shipAnswer,
  engravingItem,
} = require('./shared')

module.exports = engravingItem({
  key: 'recognitionAwards',
  slug: 'recognition-awards',
  path: '/services/laser-engraving/recognition-awards/',
  noun: 'award',
  crumb: 'Recognition awards',
  title: 'Custom Acrylic Awards & Recognition Plaques | PrintAWorld',
  description:
    'Custom acrylic awards and employee recognition plaques, laser cut, engraved and printed in NYC: sales, service and employee of the year awards.',
  eyebrow: 'Acrylic awards & plaques / made in New York',
  heading:
    'Custom acrylic awards and employee recognition plaques, made in NYC.',
  answer: `PrintAWorld makes custom acrylic awards and employee recognition plaques in New York: employee of the year, sales and years-of-service awards in acrylic, wood and metal. We laser-cut the shape, engrave the names and UV print your logo in color, all in our own studio, from one award to a whole ceremony. Engraving is a ${setup} setup plus ${each(medium, 'award')}; the award itself is ${quoted.toLowerCase()}.`,
  cta: 'Get an award quote',
  heroImage: 'walnutBrass',
  ideaImages: ['layeredColor', 'yearsOfService', 'salesAward'],
  images: {
    walnutBrass: {
      file: 'walnut-brass-award',
      alt: 'Walnut award with a brushed brass plate engraved Employee of the Year 2026 on a black base',
      caption: 'Walnut and brass, engraved',
    },
    layeredColor: {
      file: 'layered-color-award',
      alt: 'Chunky star award in layered opaque navy and white acrylic with a printed logo on a black base',
      caption: 'Layered color acrylic, cut and printed',
    },
    yearsOfService: {
      file: 'years-of-service',
      alt: 'Three matching wood and metal awards engraved 5 years, 10 years and 20 years on a shelf',
      caption: 'A matching years-of-service set',
    },
    salesAward: {
      file: 'sales-award',
      alt: 'Sculpted gold-finish upward arrow on a heavy black base with an engraved Top Sales plate',
      caption: 'A sculpted award for top sellers',
    },
  },
  intro: {
    eyebrow: 'Not from a catalog',
    title: 'Corporate awards cut, engraved and printed in-house.',
    paragraphs: [
      'Award catalogs sell the same glass shapes with your text on them. We cut the award in your shape, layer colors, print the logo and engrave each name, so it looks like your company made it.',
      'For a signature award, we 3D model and sculpt the top and mount it on an engraved base, the way we make custom trophies.',
    ],
  },
  ideas: {
    eyebrow: 'Award ideas',
    title: 'Acrylic, wood and sculpted awards people display.',
  },
  uses: {
    eyebrow: 'What they recognize',
    title: 'Employee recognition awards for every milestone.',
    items: [
      {
        label: 'Employee of the year',
        title: 'The top award of the night',
        body: 'One standout piece with the name, the year and your logo, sized to be photographed on stage.',
      },
      {
        label: 'Sales awards',
        title: 'President’s club and top sellers',
        body: 'A set for every winner, with the quarter or the number engraved on each.',
      },
      {
        label: 'Years of service',
        title: '5, 10, 20 years',
        body: 'A matching design for each milestone, so the series looks right on a shelf.',
      },
      {
        label: 'Teams and partners',
        title: 'Partner and client awards',
        body: 'Awards for partners, vendors and clients, with both logos.',
      },
      {
        label: 'Associations',
        title: 'Galas and industry awards',
        body: 'A run of awards for every category, with the names engraved once the winners are known.',
      },
    ],
  },
  materials: {
    link: 'Acrylic, wood or metal',
    eyebrow: 'Materials',
    title: 'Acrylic awards, wood awards and metal plates.',
    items: [
      {
        title: 'Color acrylic',
        body: 'Opaque and colored acrylic, laser cut to any shape and layered for depth, with your logo UV printed in color.',
      },
      {
        title: 'Wood',
        body: 'Walnut, maple and plywood, engraved dark, or cut into a shape and mounted on a base.',
      },
      {
        title: 'Metal plates',
        body: 'Brass, stainless and anodized aluminum plates engraved on our MOPA laser and mounted on wood or acrylic.',
      },
      {
        title: 'Sculpted 3D',
        body: `A 3D-printed and painted or plated top, from our custom trophy service. Custom trophies start at $${trophyPricing.from}.`,
      },
    ],
  },
  process: {
    title: 'From your logo to a table of awards.',
    note: 'Standard lead time is 2–3 weeks after you approve the design. Tell us the ceremony date first.',
    steps: [
      {
        title: 'Send the logo and list',
        body: 'Your logo, the award names, how many, and the event date.',
      },
      {
        title: 'Approve the design',
        body: 'We send a layout of the award with your text, then a sample for larger runs.',
      },
      {
        title: 'We cut and engrave',
        body: 'Each award is cut, printed and engraved, with names checked against your list.',
      },
      {
        title: 'Ready for the stage',
        body: 'Boxed and shipped, or picked up in NYC before the event.',
      },
    ],
  },
  pricing: {
    title: 'Custom award pricing, per piece.',
    cta: 'Price my awards',
    rows: [
      { label: 'Engraving, per award', value: medium },
      { label: 'The award, cut and printed', value: quoted },
      { label: 'Sculpted 3D tops', value: 'See custom trophies' },
      { label: 'Gift boxes', value: quoted },
      { label: 'Rush for an event', value: 'Case by case' },
    ],
  },
  bulk: {
    eyebrow: 'Award ceremonies',
    title: 'A full set of awards, ready for the event.',
    paragraphs: [
      'We plan back from the ceremony date. Send the categories now and the winners’ names when you have them; names can be engraved last.',
      'Need a sample for approval first? We make one award, then the rest.',
    ],
    cta: 'Plan an award order',
  },
  faqTitle: 'Custom award questions, answered.',
  faqs: [
    {
      question: 'How much do custom recognition awards cost?',
      answer: costAnswer(
        medium,
        `The award itself (cutting, layers, color printing, bases) is ${quoted.toLowerCase()}. Sculpted 3D awards are priced like our custom trophies, from $${trophyPricing.from} per piece.`
      ),
    },
    {
      question: 'Do you make crystal or glass awards?',
      answer:
        'We focus on awards we cut and make in-house: colored acrylic, wood, metal and 3D-printed sculpture. We can engrave a glass award you already have, after a test.',
    },
    {
      question: 'Can you make custom acrylic awards in our shape?',
      answer:
        'Yes. We laser cut acrylic to your logo or any outline, layer colors, and UV print the logo in full color on the same piece.',
    },
    {
      question: 'Can every award have a different name?',
      answer:
        'Yes. Send the names in a spreadsheet; the design stays the same and each award gets its own name and title.',
    },
    {
      question: 'Is there a minimum order?',
      answer: minimumAnswer('award'),
    },
    {
      question: 'How far ahead should we order?',
      answer: timingAnswer(
        'For an awards night, start three to four weeks before the event.'
      ),
    },
    {
      question: 'Do you ship?',
      answer: shipAnswer,
    },
  ],
  quote: {
    title: 'Tell us about your awards.',
    body: 'Send the logo, the award names, how many and the event date. We’ll reply with design ideas and a written quote.',
    checklist: [
      'Your logo, vector if you have it',
      'The award titles and how many of each',
      'A style you like: acrylic, wood, metal or sculpted',
      'The event date and delivery ZIP',
    ],
  },
  reviews: ['custom-trophies', 'laser-engraving'],
  moreCards: ['plaques', 'dealToys', 'corporateGifts'],
  related: [
    ['Custom trophies', '/services/custom-trophy/'],
    ['Custom awards', '/services/custom-awards/'],
    ['All laser engraving', '/services/laser-engraving/'],
    ['Laser cutting', '/services/laser-cutting/'],
    ['UV printing', '/uv-printing-services/'],
  ],
  schema: {
    name: 'Custom Acrylic Awards and Recognition Plaques',
    alternateName: [
      'Custom acrylic awards',
      'Employee recognition plaques',
      'Employee recognition awards',
      'Years of service awards',
      'Corporate awards',
    ],
    serviceType: 'Custom corporate recognition awards',
  },
})
